import axios from 'axios';

export type BrokerageProviderInfo = {
  id: string;
  name: string;
  description: string;
  docsUrl: string;
  keyUrlPaper?: string;
  keyUrlLive?: string;
  supportsPaper: boolean;
};

export type BrokerageAccountMeta = {
  last4?: string | null;
  accountHint?: string | null;
  status?: string | null;
  tradingBlocked?: boolean;
  accountBlocked?: boolean;
  currency?: string;
  optionsTradingLevel?: number | null;
  equityAtConnect?: string | null;
};

export type BrokerageConnection = {
  id: number;
  provider: string;
  mode: 'paper' | 'live' | string;
  enabled: boolean;
  keyPrefix: string;
  status: string;
  lastVerifiedAt: string | null;
  lastError: string | null;
  accountHint: string | null;
  accountMeta?: BrokerageAccountMeta | null;
  createdAt: string;
  updatedAt: string;
};

export type AlpacaAccountSummary = {
  mode?: 'paper' | 'live' | string;
  status: string | null;
  equity: string | null;
  buyingPower: string | null;
  cash: string | null;
  portfolioValue?: string | null;
  currency: string;
  accountNumberLast4?: string | null;
  accountHint?: string | null;
  /** @deprecated use accountNumberLast4 */
  accountNumber?: string | null;
  tradingBlocked?: boolean;
  accountBlocked?: boolean;
  tradeSuspendedByUser?: boolean;
  patternDayTrader?: boolean;
  shortingEnabled?: boolean;
  multiplier?: string | null;
  optionsTradingLevel?: number | null;
  cryptoStatus?: string | null;
};

export async function fetchBrokerageConnections(): Promise<{
  connections: BrokerageConnection[];
  providers: BrokerageProviderInfo[];
}> {
  const { data } = await axios.get('/brokerage/connections');
  return data;
}

export async function previewAlpaca(input: {
  apiKeyId: string;
  apiSecret: string;
  paper?: boolean;
}): Promise<{
  preview: true;
  keyPrefix: string;
  account: AlpacaAccountSummary;
  warnings: string[];
}> {
  const { data } = await axios.post('/brokerage/connections/alpaca/preview', {
    apiKeyId: input.apiKeyId,
    apiSecret: input.apiSecret,
    paper: input.paper !== false
  });
  return data;
}

export async function connectAlpaca(input: {
  apiKeyId: string;
  apiSecret: string;
  paper?: boolean;
  confirmLive?: boolean;
  confirmAccountLast4?: string | null;
}): Promise<{
  connection: BrokerageConnection;
  account: AlpacaAccountSummary;
  warnings?: string[];
}> {
  const { data } = await axios.put('/brokerage/connections/alpaca', {
    apiKeyId: input.apiKeyId,
    apiSecret: input.apiSecret,
    paper: input.paper !== false,
    confirmLive: input.confirmLive === true,
    confirmAccountLast4: input.confirmAccountLast4 || undefined
  });
  return data;
}

export async function verifyAlpaca(): Promise<{
  connection: BrokerageConnection;
  account: AlpacaAccountSummary;
  warnings?: string[];
}> {
  const { data } = await axios.post('/brokerage/connections/alpaca/verify');
  return data;
}

export async function fetchAlpacaAccount(): Promise<{
  connection: BrokerageConnection;
  account: AlpacaAccountSummary;
  warnings?: string[];
}> {
  const { data } = await axios.get('/brokerage/connections/alpaca/account');
  return data;
}

export async function setAlpacaEnabled(enabled: boolean): Promise<BrokerageConnection> {
  const { data } = await axios.patch('/brokerage/connections/alpaca', { enabled });
  return data.connection;
}

export async function disconnectAlpaca(): Promise<void> {
  await axios.delete('/brokerage/connections/alpaca');
}
